/**
 * L'Hôtel des Ventes (GDD §20). Module pur.
 *
 * Deux marchés dans un seul écran, et c'est délibéré.
 *
 * **Les annonces entre joueurs** sont ce qu'on veut vraiment : « j'ai forgemagé
 * une pièce, je la vends », « je farme une ressource dont tu n'as pas le
 * courage ». C'est la forge (§19) qui leur donne de la marchandise ; sans elle
 * on n'échangerait que du minerai.
 *
 * **Le PNJ teneur de marché** achète et vend en permanence. Ce n'est pas un
 * pis-aller : avec deux joueurs actifs, un marché strictement entre joueurs est
 * une salle vide, l'annonce reste en vitrine une semaine et la fonctionnalité
 * passe pour cassée. Le PNJ est le plancher de liquidité ; il s'efface tout
 * seul à mesure que des joueurs arrivent, puisque leurs annonces sont moins
 * chères que les siennes.
 *
 * Le PNJ est aussi le **puits à kamas** : il rachète au prix du §7 et revend
 * bien plus cher, et son prix monte à chaque unité déjà vendue. L'aller-retour
 * est toujours perdant, donc on ne peut pas s'enrichir en le faisant tourner.
 */
import type { Contenu, Item } from "./contenu.ts"
import { tempsPourUn } from "./rarete.ts"

import { perfection } from "./objets.ts"

/**
 * Ce que le comptoir prend au-dessus du temps qu'il fait gagner.
 *
 * Trois fois : on paie trois heures de revenu pour s'épargner une heure de
 * farm. Cher — c'est voulu. Le comptoir est un **puits à kamas**, et il ne doit
 * jamais devenir la façon normale de s'équiper.
 *
 * La conséquence est saine : à revenu égal, il n'intéresse personne, et il
 * devient une bonne affaire dès qu'on est très au-dessus du niveau de ce qu'on
 * achète. C'est exactement le besoin — « je n'ai pas le courage de retourner
 * farmer un ingrédient de niveau 50 ».
 */
export const MARGE_PNJ = 3

/**
 * Kamas gagnés par seconde de jeu, à un niveau donné.
 *
 * Mesuré moteur en main, six personnages en combat sur la meilleure famille
 * tenable : 163 k/h à niveau 20, 340 k à 50, 655 k à 100, 1,02 M à 150,
 * 1,39 M à 200. Rapporté au niveau, le rapport reste entre 1,8 et 2,25 — d'où
 * cette droite, qui vaut mieux qu'une constante puisqu'un joueur qui farme une
 * ressource de niveau N a, en général, le revenu d'un joueur de niveau N.
 */
export const KAMAS_PAR_S = (niveau: number) => 1.9 * Math.max(1, niveau)

/**
 * Au-delà, l'objet n'est plus une marchandise mais une quête.
 *
 * Certaines chaînes de fabrication, dépliées, dépassent le millier d'heures. Les
 * facturer au prix fort donnerait des nombres sans signification ; on borne, et
 * elles restent de toute façon hors de portée.
 */
export const TEMPS_MAX_S = 24 * 3600

/**
 * Tous les combien le prix du comptoir augmente d'un cran.
 *
 * Sans cette montée, un joueur riche viderait la boutique et court-circuiterait
 * la récolte entière. Avec elle, le comptoir dépanne mais ne remplace jamais le
 * fait d'aller chercher soi-même : la centième unité coûte le double de la
 * première.
 */
export const PALIER_PNJ = 100

/**
 * Ce que le comptoir demande pour la prochaine unité.
 *
 * `null` quand l'objet ne s'obtient pas en jouant — on ne vend pas ce que le
 * jeu ne donne pas.
 */
export function prixPNJ(c: Contenu, it: Item, deja = 0): number | null {
  const t = tempsPourUn(c, it)
  if (t === null) return null
  const secondes = Math.min(TEMPS_MAX_S, t)
  const base = secondes * KAMAS_PAR_S(it.niveau) * MARGE_PNJ
  return Math.max(1, Math.round(base * (1 + Math.max(0, deja) / PALIER_PNJ)))
}

/** Ce que coûte un lot de `n` unités, la montée du prix comprise. */
export function prixPNJLot(c: Contenu, it: Item, n: number, deja = 0): number | null {
  const un = prixPNJ(c, it, deja)
  if (un === null) return null
  let total = 0
  for (let k = 0; k < n; k++) total += prixPNJ(c, it, deja + k)!
  return total
}

/**
 * La part prélevée sur chaque vente entre joueurs.
 *
 * Elle est **détruite**, pas reversée : c'est un second puits, et c'est ce qui
 * empêche le marché de devenir une pompe à kamas entre deux comptes complices.
 * Deux pour cent, comme à l'Hôtel des Ventes de Dofus — assez pour compter sur
 * la durée, trop peu pour décourager une vente.
 */
export const TAXE = 0.02

export const taxeDe = (prix: number) => Math.floor(prix * TAXE)
export const netVendeur = (prix: number) => prix - taxeDe(prix)

// ---------------------------------------------------------------- annonces

export interface Annonce {
  id: number
  vendeur: string
  /** pseudonyme du vendeur, ou null pour le PNJ */
  pseudo: string | null
  item: number
  quantite: number
  /** le jet, pour une pièce d'équipement ; absent pour un lot de ressources */
  jet: Record<number, number> | null
  /** prix **total** du lot, pas à l'unité */
  prix: number
  niveau: number
  perfection: number | null
  deposeLe: number
}

/**
 * Ce qu'une annonce vaut aux yeux du serveur, indépendamment du prix demandé.
 *
 * C'est cette valeur-là qu'on impute à l'enveloppe de plausibilité, jamais le
 * prix affiché : sinon il suffirait de mettre une ceinture pourrie à cent
 * millions pour s'ouvrir un droit de dépôt illimité.
 *
 * Elle se compte en **temps de jeu**, comme le comptoir, et pour la même
 * raison : `prixVente` ne connaît que le niveau de l'objet. Un ingrédient qui
 * tombe à 2 % pesait autant qu'une babiole ramassée par terre, si bien qu'un
 * client bricolé pouvait déverser des objets rares sans jamais entamer son
 * enveloppe. On facture donc ce que la chose coûte à produire.
 */
export function valeurReelle(c: Contenu, it: Item, quantite: number, jet: Record<number, number> | null): number {
  const t = Math.min(TEMPS_MAX_S, tempsPourUn(c, it) ?? 60)
  const base = t * KAMAS_PAR_S(it.niveau) * Math.max(1, quantite)
  if (!jet) return Math.round(base)
  // Une pièce parfaite vaut plus qu'un rebut : la forge doit se voir au bilan.
  const pct = perfection(it, { uid: "", item: it.id, jet })
  return Math.round(base * (1 + pct / 100))
}

/** Les bornes d'un prix demandé. On laisse le vendeur libre, dans du raisonnable. */
export const PRIX_MIN = 1
export const PRIX_MAX = 2_000_000_000

export type Refus =
  | "prix" | "quantite" | "introuvable" | "enveloppe" | "trop-d-annonces" | "pas-a-vendre"

/** Au-delà, on encombre le marché plus qu'on ne l'alimente. */
export const ANNONCES_MAX = 30

export interface Depot {
  item: number
  quantite: number
  jet: Record<number, number> | null
  prix: number
}

/**
 * Un dépôt est-il recevable ?
 *
 * `enveloppe` est ce que le compte pouvait produire depuis toujours, `dejaDepose`
 * ce qu'il a déjà mis en vente net de ses achats. La règle tient en une ligne :
 * **on ne peut pas déposer plus de valeur qu'on ne pouvait en produire.**
 */
export function refusDepot(
  c: Contenu, it: Item | null, d: Depot, enveloppe: number, dejaDepose: number, annonces: number,
): Refus | null {
  if (!it) return "introuvable"
  if (!Number.isInteger(d.quantite) || d.quantite < 1 || d.quantite > 10_000) return "quantite"
  if (!Number.isInteger(d.prix) || d.prix < PRIX_MIN || d.prix > PRIX_MAX) return "prix"
  if (annonces >= ANNONCES_MAX) return "trop-d-annonces"
  if (dejaDepose + valeurReelle(c, it, d.quantite, d.jet) > enveloppe) return "enveloppe"
  return null
}

/** Ce que voit l'acheteur : le prix à l'unité, pour comparer des lots inégaux. */
export const prixUnitaire = (a: Annonce) => Math.round(a.prix / Math.max(1, a.quantite))
